import { useState } from 'react'
import { useCreateCalibrationProfile } from '../../hooks/useCalibrationProfiles'
import { useAuth } from '../../hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { Alert, AlertDescription } from '../ui/alert'
import { Badge } from '../ui/badge'
import { RunWithDetails } from '../../types'
import { calculateSegmentPace, formatPace, formatTime } from '../../lib/calculations'
import { Info, Timer, TrendingUp, TrendingDown, Zap, Mountain } from 'lucide-react'

interface ManualProfileCreatorProps {
  onProfileCreated: () => void
  run?: RunWithDetails
}

export function ManualProfileCreator({ onProfileCreated, run }: ManualProfileCreatorProps) {
  const { user } = useAuth()
  const createProfileMutation = useCreateCalibrationProfile()
  
  // Form state with sensible defaults
  const [profileName, setProfileName] = useState('')
  const [basePace, setBasePace] = useState(6.42) // min/km
  const [uphillFactor, setUphillFactor] = useState(3.5) // seconds per 100m/km
  const [baseDownhillFactor, setBaseDownhillFactor] = useState(1.2) // Base downhill bonus
  const [technicality, setTechnicality] = useState(1) // 0=easy, 1=moderate, 2=technical
  const [useFatigue, setUseFatigue] = useState(false)
  const [fatigueFactor, setFatigueFactor] = useState(0.0) // seconds per km
  const [error, setError] = useState<string | null>(null)

  // Technicality scaling - reduces downhill advantage on technical terrain
  const technicalityLabels = ['Easy/Runnable', 'Moderate', 'Technical/Rocky']
  const technicalityMultipliers = [1.0, 0.7, 0.4] // How much downhill advantage you keep
  const effectiveDownhillFactor = baseDownhillFactor * technicalityMultipliers[technicality]

  // Convert to internal units for calculations and preview
  const profileValues = {
    beta0_base_pace_min_per_km: basePace,
    beta1_uphill_min_per_m_per_km: uphillFactor / 100, // Convert to per-meter
    beta2_downhill_min_per_m_per_km: -effectiveDownhillFactor / 100, // Negative for speed bonus, scaled by technicality
    beta3_fatigue_min_per_km: fatigueFactor / 60, // Convert seconds to minutes
    use_fatigue: useFatigue
  }

  // Calculate preview for current run segments
  const previewData = run?.segments ? run.segments.slice(0, 3).map((segment, index) => {
    const pace = calculateSegmentPace(segment, profileValues)
    const time = pace * segment.distance_km
    return {
      segment,
      index,
      pace,
      time
    }
  }) : []

  const handleCreateProfile = async () => {
    if (!user || !profileName.trim()) {
      setError('Profile name is required')
      return
    }

    if (basePace < 3 || basePace > 20) {
      setError('Base pace must be between 3:00 and 20:00 per km')
      return
    }

    try {
      setError(null)
      
      await createProfileMutation.mutateAsync({
        user_id: user.id,
        name: profileName.trim(),
        beta0_base_pace_min_per_km: profileValues.beta0_base_pace_min_per_km,
        beta1_uphill_min_per_m_per_km: profileValues.beta1_uphill_min_per_m_per_km,
        beta2_downhill_min_per_m_per_km: profileValues.beta2_downhill_min_per_m_per_km,
        beta3_fatigue_min_per_km: profileValues.beta3_fatigue_min_per_km,
        use_fatigue: useFatigue,
        // Default nutrition targets
        target_carbs_g_per_h_low: 60.0,
        target_carbs_g_per_h_high: 90.0,
        target_fluids_ml_per_h_low: 500.0,
        target_fluids_ml_per_h_high: 750.0,
        target_sodium_mg_per_h_low: 300.0,
        target_sodium_mg_per_h_high: 800.0
      })
      
      onProfileCreated()
    } catch (err) {
      setError('Failed to create calibration profile')
    }
  }

  const resetToDefaults = () => {
    setBasePace(6.42)
    setUphillFactor(3.5)
    setBaseDownhillFactor(1.2)
    setTechnicality(1)
    setUseFatigue(false)
    setFatigueFactor(0.0)
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-xl font-bold text-primary-900">
            <Timer className="h-6 w-6 text-primary-600" />
            Create Custom Profile
          </CardTitle>
          <p className="text-sm text-neutral-600">
            Manually configure your pace model by adjusting the parameters below
          </p>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Profile Name */}
          <div>
            <Label htmlFor="profileName" className="text-neutral-700 font-medium">Profile Name *</Label>
            <Input
              id="profileName"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="e.g., My Trail Running Pace"
              className="mt-2"
            />
          </div>

          {/* Base Pace */}
          <div className="space-y-3 p-4 bg-neutral-50 rounded-xl">
            <Label className="flex items-center gap-2 text-neutral-700 font-medium">
              <Timer className="h-5 w-5 text-primary-600" />
              Base Pace (flat terrain)
            </Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="3.0"
                max="20.0"
                value={basePace}
                onChange={(e) => setBasePace(parseFloat(e.target.value) || 6.42)}
                className="w-32 font-mono bg-white"
              />
              <span className="text-sm text-neutral-600">min/km</span>
              <Badge className="ml-2 bg-primary-100 text-primary-800">
                {formatPace(basePace)}/km
              </Badge>
            </div>
            <p className="text-xs text-neutral-500">Your comfortable pace on flat, easy terrain</p>
          </div>

          {/* Uphill Factor */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-red-500" />
              Uphill Penalty
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={uphillFactor}
                onChange={(e) => setUphillFactor(parseFloat(e.target.value) || 3.5)}
                className="w-32 font-mono"
              />
              <span className="text-sm text-gray-500">seconds per 100m/km</span>
            </div>
            <p className="text-xs text-gray-500">How much slower you get on climbs (higher = more affected by hills)</p>
          </div>

          {/* Downhill Factor */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-green-500" />
              Downhill Bonus
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={baseDownhillFactor}
                onChange={(e) => setBaseDownhillFactor(parseFloat(e.target.value) || 1.2)}
                className="w-32 font-mono"
              />
              <span className="text-sm text-gray-500">seconds per 100m/km</span>
            </div>
            <p className="text-xs text-gray-500">How much faster you get on descents (higher = more speed gain)</p>
          </div>

          {/* Technicality Slider */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mountain className="h-4 w-4 text-purple-500" />
              Terrain Technicality
            </Label>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500 w-20">Easy</span>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="1"
                  value={technicality}
                  onChange={(e) => setTechnicality(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <span className="text-xs text-gray-500 w-20 text-right">Technical</span>
              </div>
              <div className="flex justify-between items-center">
                <Badge variant="outline" className="text-xs">
                  {technicalityLabels[technicality]}
                </Badge>
                <span className="text-xs text-gray-500 font-mono">
                  Effective: {(effectiveDownhillFactor).toFixed(1)}s per 100m/km
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Technical terrain reduces downhill speed advantage (rocks, roots, etc.)
            </p>
          </div>

          {/* Fatigue Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-orange-500" />
                Enable Fatigue Factor
              </Label>
              <Switch
                checked={useFatigue}
                onCheckedChange={setUseFatigue}
              />
            </div>
            
            {useFatigue && (
              <div className="space-y-2 ml-6">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={fatigueFactor}
                    onChange={(e) => setFatigueFactor(parseFloat(e.target.value) || 0)}
                    className="w-32 font-mono"
                  />
                  <span className="text-sm text-gray-500">seconds per km</span>
                </div>
                <p className="text-xs text-gray-500">How much you slow down per km of running (cumulative fatigue)</p>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="border-accent-200 bg-accent-50">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-4">
            <Button onClick={resetToDefaults} variant="outline" className="flex-1" size="lg">
              Reset to Defaults
            </Button>
            <Button
              onClick={handleCreateProfile}
              disabled={!profileName.trim() || createProfileMutation.isPending}
              className="flex-1"
              size="lg"
            >
              {createProfileMutation.isPending ? 'Creating...' : 'Create Profile'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Section */}
      {run && run.segments && run.segments.length > 0 && (
        <Card className="bg-primary-50 border-primary-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-primary-900 flex items-center gap-3">
              <Info className="h-5 w-5 text-primary-600" />
              Preview on Current Run
            </CardTitle>
            <p className="text-sm text-primary-800">
              See how your custom profile affects the first few segments
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {previewData.map(({ segment, index, pace, time }) => (
                <div key={segment.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-primary-100">
                  <div>
                    <div className="font-medium text-primary-900">
                      {segment.label || `Segment ${index + 1}`}
                    </div>
                    <div className="text-sm text-primary-700">
                      {segment.distance_km}km • +{segment.elev_gain_m || 0}m • -{segment.elev_loss_m || 0}m
                    </div>
                  </div>
                  <div className="text-right font-mono">
                    <div className="font-semibold text-primary-900">{formatPace(pace)}/km</div>
                    <div className="text-sm text-primary-700">{formatTime(time)}</div>
                  </div>
                </div>
              ))}

              {run.segments.length > 3 && (
                <div className="text-center text-sm text-primary-600 pt-2">
                  ... and {run.segments.length - 3} more segments
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}